import { useState, useEffect, useRef, useCallback } from 'react'
import { api } from '../lib/api'

export interface MinigameSession {
  session_id?: number
  active: boolean
  question?: string
  option_a?: string
  option_b?: string
  my_answer: 'A' | 'B' | null
  other_answered: boolean
  expires_at?: string
  revealed?: boolean
}

interface UseMinigameReturn {
  minigameActive: boolean
  minigameSession: MinigameSession | null
  minigameTimeLeft: number
  handleStartMinigame: () => Promise<void>
  handleMinigameAnswer: (choice: 'A' | 'B') => Promise<void>
  checkForMinigameSession: () => Promise<void>
  dismissMinigame: () => void
}

export function useMinigame(
  matchId: string | undefined,
  fetchMessagesQuietly: () => Promise<void>,
  showAlert: (msg: string) => void
): UseMinigameReturn {
  const [minigameActive, setMinigameActive] = useState(false)
  const [minigameSession, setMinigameSession] = useState<MinigameSession | null>(null)
  const [minigameTimeLeft, setMinigameTimeLeft] = useState(10)
  const [dismissedSessionId, setDismissedSessionId] = useState<number | null>(null)
  const minigameTimerRef = useRef<number | null>(null)

  const startMinigameTimer = useCallback((expiresAt: number) => {
    if (minigameTimerRef.current) {
      clearInterval(minigameTimerRef.current)
    }

    minigameTimerRef.current = window.setInterval(() => {
      const now = Date.now()
      const timeLeft = Math.max(0, Math.floor((expiresAt - now) / 1000))
      setMinigameTimeLeft(timeLeft)

      if (timeLeft <= 0) {
        if (minigameTimerRef.current) {
          clearInterval(minigameTimerRef.current)
        }
        setMinigameActive(false)
        setMinigameSession(null)
      }
    }, 100)
  }, [])

  const checkForMinigameSession = useCallback(async () => {
    if (!matchId) return
    try {
      const response = await api.matches.getMinigameSession(parseInt(matchId))
      if (response.active) {
        if (response.revealed) {
          setMinigameActive(false)
          setMinigameSession(null)
          return
        }

        if (response.my_answer) {
          setMinigameActive(false)
          setMinigameSession(null)
          return
        }

        if (response.session_id && response.session_id === dismissedSessionId) {
          return
        }

        const sessionData: MinigameSession = {
          ...response,
          my_answer: (response.my_answer as 'A' | 'B' | null) || null,
          other_answered: response.other_answered || false
        }

        setMinigameActive(true)
        setMinigameSession(sessionData)

        const expiresAt = response.expires_at ? new Date(response.expires_at).getTime() : Date.now()
        const now = Date.now()
        const timeLeft = Math.max(0, Math.floor((expiresAt - now) / 1000))
        setMinigameTimeLeft(timeLeft)

        if (timeLeft > 0) {
          startMinigameTimer(expiresAt)
        }
      } else {
        setMinigameActive(false)
        setMinigameSession(null)
        setDismissedSessionId(null)
      }
    } catch {
      // Silent fail
    }
  }, [matchId, dismissedSessionId, startMinigameTimer])

  const handleMinigameAnswer = useCallback(async (choice: 'A' | 'B') => {
    if (!minigameSession || minigameSession.my_answer || !matchId) return

    try {
      const response = await api.matches.submitMinigameAnswer(parseInt(matchId), choice)

      if (response.revealed) {
        await fetchMessagesQuietly()
        setMinigameActive(false)
        setMinigameSession(null)
        if (minigameTimerRef.current) {
          clearInterval(minigameTimerRef.current)
        }
      } else {
        setMinigameActive(false)
        setMinigameSession(null)
        if (minigameTimerRef.current) {
          clearInterval(minigameTimerRef.current)
        }
      }
    } catch {
      showAlert('답변 제출에 실패했습니다.')
    }
  }, [minigameSession, matchId, fetchMessagesQuietly, showAlert])

  // 미니게임 무료화 - 모달 없이 바로 시작
  const handleStartMinigame = useCallback(async () => {
    if (!matchId) return
    try {
      await api.matches.startMinigame(parseInt(matchId))
      await checkForMinigameSession()
    } catch (error: any) {
      if (error.error === 'A game session is already active') {
        showAlert('이미 진행 중인 게임이 있습니다.')
      } else if (error.message?.includes('상대방이 지금 게임을 시작할 수 없어요')) {
        showAlert('상대방이 지금 게임을 시작할 수 없어요')
      } else {
        showAlert(error.message || '게임 시작에 실패했습니다.')
      }
    }
  }, [matchId, checkForMinigameSession, showAlert])

  const dismissMinigame = useCallback(() => {
    setMinigameActive(false)
    setDismissedSessionId(minigameSession?.session_id || null)
  }, [minigameSession])

  // Initial check
  useEffect(() => {
    if (!matchId) return
    checkForMinigameSession()

    return () => {
      if (minigameTimerRef.current) {
        clearInterval(minigameTimerRef.current)
      }
    }
  }, [matchId, checkForMinigameSession])

  return {
    minigameActive,
    minigameSession,
    minigameTimeLeft,
    handleStartMinigame,
    handleMinigameAnswer,
    checkForMinigameSession,
    dismissMinigame,
  }
}
